import { useEffect, useState, Component, lazy, Suspense } from "react";
import { AnimatePresence } from "framer-motion";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./context/LanguageContext";
import CustomCursor from "./components/CustomCursor";
import Preloader from "./components/Preloader";
import Header from "./components/Header";
import Hero from "./components/Hero";
import HomeIntro from "./components/HomeIntro";
import Portfolio from "./components/Portfolio";
import Showroom from "./components/Showroom";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import InviteTeaser from "./components/InviteTeaser";
import Seo from "./components/Seo";

// code splitting: solo le rotte secondarie sono caricate on-demand
const ProjectDetail = lazy(() => import("./components/ProjectDetail"));
const Admin = lazy(() => import("./components/Admin"));
const PartnerCategory = lazy(() => import("./components/PartnerCategory"));
const ServiceInterior = lazy(() => import("./components/ServiceInterior"));
const ServiceContracting = lazy(() => import("./components/ServiceContracting"));
const ServiceShowroom = lazy(() => import("./components/ServiceShowroom"));
const ServiceOmniway = lazy(() => import("./components/ServiceOmniway"));
const ShowroomPage = lazy(() => import("./components/ShowroomPage"));
const ChiSiamoPage = lazy(() => import("./components/ChiSiamoPage"));
const MetodoPage = lazy(() => import("./components/MetodoPage"));
const WorksPage = lazy(() => import("./components/WorksPage"));
const ContattiPage = lazy(() => import("./components/ContattiPage"));
const InvitePage = lazy(() => import("./components/InvitePage"));
import useLenisInit from "./lib/useLenis";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 48, color: "#F2F0E9", background: "#0A0A09", fontFamily: "sans-serif" }}>
          Qualcosa è andato storto. Ricarica la pagina.
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const location = useLocation();
  const { pathname } = location;
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    if (location.state?.scrollTo) {
      const target = location.state.scrollTo.replace(/^#/, "");
      const t0 = Date.now();
      let lastH = -1;
      let stable = 0;
      const iv = setInterval(() => {
        const el = document.getElementById(target);
        const h = document.documentElement.scrollHeight;
        stable = h === lastH ? stable + 1 : 0;
        lastH = h;
        if (el && stable >= 2) {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
          else el.scrollIntoView({ behavior: "smooth" });
          clearInterval(iv);
        } else if (Date.now() - t0 > 6000) {
          clearInterval(iv);
        }
      }, 200);
      return () => clearInterval(iv);
    }
  }, [location.state]);
  return null;
}

function NavigateBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    window.__navigate = navigate;
    return () => {
      window.__navigate = null;
    };
  }, [navigate]);
  return null;
}

function Shell({ children }) {
  return (
    <div className="bg-ink text-cream font-sans overflow-x-clip">
      <div className="grain" aria-hidden="true" />
      <CustomCursor />
      <Header />
      <main>{children}</main>
      <Footer />
      <CookieBanner />
      <Toaster position="bottom-right" />
    </div>
  );
}

let hasLoadedOnce = false;

function Site() {
  const [loading, setLoading] = useState(!hasLoadedOnce);
  useLenisInit();
  const location = useLocation();

  useEffect(() => {
    hasLoadedOnce = true;
    if (!loading) return;
    const timer = setTimeout(() => setLoading(false), 1600);
    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    const saved = sessionStorage.getItem("gg_home_scroll");
    if (!saved || location.key === "default") return;
    const y = parseInt(saved, 10);
    const timer = setTimeout(() => {
      if (window.__lenis) window.__lenis.scrollTo(y, { immediate: true });
      else window.scrollTo(0, y);
      sessionStorage.removeItem("gg_home_scroll");
    }, 400);
    return () => clearTimeout(timer);
  }, [location.key]);

  return (
    <Shell>
      <AnimatePresence>{loading && <Preloader key="preloader" />}</AnimatePresence>
      <Hero />
      <HomeIntro />
      <Portfolio />
      <Showroom />
      <Contact />
      <InviteTeaser />
    </Shell>
  );
}

function ProjectPage() {
  useLenisInit();
  return (
    <Shell>
      <ProjectDetail />
    </Shell>
  );
}

function PartnerPage() {
  useLenisInit();
  return (
    <Shell>
      <PartnerCategory />
    </Shell>
  );
}

function ServicePage({ children }) {
  useLenisInit();
  return <Shell>{children}</Shell>;
}

function InviteRoute() {
  useLenisInit();
  return <InvitePage />;
}

function ImageGuard() {
  useEffect(() => {
    const prevent = (e) => {
      if (e.target && e.target.tagName === "IMG") {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
    document.addEventListener("contextmenu", prevent);
    document.addEventListener("dragstart", prevent);
    return () => {
      document.removeEventListener("contextmenu", prevent);
      document.removeEventListener("dragstart", prevent);
    };
  }, []);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ImageGuard />
          <NavigateBridge />
          <Seo />
          <Suspense
            fallback={
              <div
                style={{ minHeight: "100vh", background: "#0A0A09" }}
                aria-label="Caricamento"
              />
            }
          >
          <Routes>
            <Route path="/" element={<Site />} />
            <Route path="/progetti/:slug" element={<ProjectPage />} />
            <Route path="/partner/:key" element={<PartnerPage />} />
            <Route path="/servizi/architettura-interior-design" element={<ServicePage><ServiceInterior /></ServicePage>} />
            <Route path="/servizi/general-contracting" element={<ServicePage><ServiceContracting /></ServicePage>} />
            <Route path="/servizi/showroom" element={<ServicePage><ServiceShowroom /></ServicePage>} />
            <Route path="/servizi/omniway" element={<ServicePage><ServiceOmniway /></ServicePage>} />
            <Route path="/showroom" element={<ServicePage><ShowroomPage /></ServicePage>} />
            <Route path="/chi-siamo" element={<ServicePage><ChiSiamoPage /></ServicePage>} />
            <Route path="/metodo" element={<ServicePage><MetodoPage /></ServicePage>} />
            <Route path="/works" element={<ServicePage><WorksPage /></ServicePage>} />
            <Route path="/contatti" element={<ServicePage><ContattiPage /></ServicePage>} />
            <Route path="/invito" element={<InviteRoute />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Site />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
